import {
  Card,
  DataStatus,
  Flag,
  Form,
  FormField,
  FormSubmitHandler,
  Label,
  showNotification,
} from 'platform/components';
import {GridItem, Box, Grid, Show, Text} from 'platform/foundation';
import {useFormatCurrency, useDateTimeFormatter} from 'platform/locale';
import {z} from 'zod';

import {isNil} from 'ramda';

import {useGetBranchQuery} from '@dms/api/core';
import {useGetCashRegisterQuery, usePatchCashRegisterMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter, SettingsSection, useSeriesOptions} from '@dms/settings/shared';
import {EMPTY_PLACEHOLDER, handleApiError} from '@dms/shared';

import {
  parseDate,
  Nullish,
  useNavigate,
  RequiredTestIdProps,
  suffixTestId,
  ApiError,
} from 'shared/web';

type FormValues = z.infer<typeof schema>;

interface InUseCashRegisterEditProps extends RequiredTestIdProps {
  cashRegisterId: string | Nullish;
}

export function InUseCashRegisterEdit(props: InUseCashRegisterEditProps) {
  const navigate = useNavigate();
  const {groupedSeries, isSeriesError, isSeriesLoading} = useSeriesOptions([
    'accounting/cash_register_document',
  ]);
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();

  const [patchCashRegister] = usePatchCashRegisterMutation();

  const {
    data: cashRegister,
    isLoading: isCashRegisterLoading,
    isError: isCashRegisterError,
  } = useGetCashRegisterQuery(
    {cashRegisterId: props.cashRegisterId ?? ''},
    {skip: isNil(props.cashRegisterId)}
  );
  const {data: branch, isLoading: isBranchLoading} = useGetBranchQuery(
    {branchId: cashRegister?.branchId ?? ''},
    {skip: isNil(cashRegister?.branchId)}
  );

  const isLoading = isCashRegisterLoading || isBranchLoading || isSeriesLoading;
  const isError = isCashRegisterError || isSeriesError;

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNil(props.cashRegisterId)) {
      showNotification.error();
      return;
    }

    await patchCashRegister({
      cashRegisterId: props.cashRegisterId,
      patchCashRegisterRequestBody: data,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.cashRegisters);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: Partial<FormValues> = {
    name: cashRegister?.name,
    expenseSeriesId: cashRegister?.expenseSeriesId,
    incomeSeriesId: cashRegister?.incomeSeriesId,
    eligibleForCardPayments: cashRegister?.eligibleForCardPayments,
    cashRegisterAccount: cashRegister?.cashRegisterAccount,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<FormValues>
        defaultValues={defaultValues}
        experimentalZodSchema={schema}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => {
          formApi.register('eligibleForCardPayments');
          const onChange = (val: boolean) => formApi.setValue('eligibleForCardPayments', val);
          const value = formApi.watch('eligibleForCardPayments') ?? false;

          return (
            <SettingsSection>
              <Grid columns={2}>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="name"
                    label={i18n.t('entity.cashRegister.parameters.description')}
                    isRequired
                    data-testid={suffixTestId('name', props)}
                  />
                </GridItem>
                <GridItem span={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="cashRegisterAccount"
                    label={i18n.t('entity.bankAccount.labels.number')}
                    maxLength={9}
                    isRequired
                    data-testid={suffixTestId('cashRegisterAccount', props)}
                  />
                </GridItem>
                <FormField
                  control={control}
                  type="choice"
                  name="expenseSeriesId"
                  isRequired
                  isNotClearable
                  options={groupedSeries['accounting/cash_register_document'] ?? []}
                  label={i18n.t('entity.cashRegister.parameters.expenseSeries')}
                  data-testid={suffixTestId('expenseSeries', props)}
                />

                <FormField
                  control={control}
                  type="choice"
                  name="incomeSeriesId"
                  isRequired
                  isNotClearable
                  options={groupedSeries['accounting/cash_register_document'] ?? []}
                  label={i18n.t('entity.cashRegister.parameters.incomeSeries')}
                  data-testid={suffixTestId('incomeSeries', props)}
                />

                <GridItem span={2}>
                  <Card variant="inlineGrey">
                    <Grid columns={5} align="center">
                      <Box>
                        <Label>{i18n.t('entity.cashRegister.parameters.code')}</Label>
                      </Box>
                      <Box>
                        <Label>{i18n.t('entity.cashRegister.parameters.currency')}</Label>
                      </Box>
                      <Box>
                        <Label>{i18n.t('entity.cashRegister.parameters.currentState')}</Label>
                      </Box>
                      <Box>
                        <Label>{i18n.t('entity.cashRegister.parameters.asOf')}</Label>
                      </Box>
                      <Box>
                        <Label>{i18n.t('entity.cashRegister.parameters.branch')}</Label>
                      </Box>

                      <Show when={cashRegister}>
                        {(cashRegister) => (
                          <>
                            <Text size="small">{cashRegister.code}</Text>
                            <Flag label={cashRegister.currency} colorScheme="blue" isSubtle />
                            <Text size="small">
                              {formatCurrency(
                                parseFloat(cashRegister.currentBalance.amount),
                                cashRegister.currentBalance.currency
                              )}
                            </Text>
                            <Text size="small">
                              {formatDateTime('dateShort', parseDate(cashRegister.activeFrom))}
                            </Text>
                            <Text size="small">{branch?.name ?? EMPTY_PLACEHOLDER}</Text>
                          </>
                        )}
                      </Show>
                    </Grid>
                  </Card>
                </GridItem>

                <GridItem span={2}>
                  <Card
                    title={i18n.t('entity.cashRegister.parameters.eligibleForCardPayments')}
                    control={{type: 'switch', value, onChange}}
                    isExpanded={false}
                    data-testid={suffixTestId('eligibleForCardPayments', props)}
                  />
                </GridItem>
                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: i18n.t('general.actions.close'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.cashRegisters),
                      'data-testid': suffixTestId('discard', props),
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: i18n.t('general.actions.edit'),
                      'data-testid': suffixTestId('save', props),
                    },
                  ]}
                />
              </Grid>
            </SettingsSection>
          );
        }}
      </Form>
    </DataStatus>
  );
}

const schema = z.object({
  name: z.string(),
  cashRegisterAccount: z
    .string()
    .regex(/^\d+$/, i18n.t('general.validations.mustBeNumber'))
    .min(6)
    .max(9)
    .nullable(),
  incomeSeriesId: z.string(),
  expenseSeriesId: z.string(),
  eligibleForCardPayments: z.boolean().nullable(),
});
